/**
 * {Loved one}'s Journey — the family's story, not a file cabinet.
 * Originals preserved · Lane reviews every upload · nothing applies
 * without the caregiver's approval.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { JourneyViewToggle } from "@/components/JourneyViewToggle";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Upload, Download, Trash2, Check, FileText, Clock, Users, Pill,
    Scale, Sparkles, Loader2, ChevronRight,
} from "lucide-react";

const TABS = [
    { key: "timeline", label: "Timeline", Icon: Clock },
    { key: "documents", label: "Documents", Icon: FileText },
    { key: "providers", label: "Care Team", Icon: Users },
    { key: "medications", label: "Medications", Icon: Pill },
    { key: "legal", label: "Legal & Planning", Icon: Scale },
    { key: "summary", label: "AI Summary", Icon: Sparkles },
];
const LEGAL_CATS = ["Legal Documents", "Advance Directives", "Power of Attorney", "Insurance"];
const SHARE_LABELS = {
    only_me: "Only me", all_care_team: "All care team", neurology: "Neurology",
    primary_care: "Primary care", guide: "GUIDE", home_health: "Home health", hospice: "Hospice",
};

export default function LovedStory() {
    const sid = getSessionId();
    const [name, setName] = useState("");
    const [tab, setTab] = useState("documents");
    const [docs, setDocs] = useState(null);
    const [cats, setCats] = useState({ categories: [], custom: [] });
    const [events, setEvents] = useState(null);
    const [providers, setProviders] = useState(null);
    const [meds, setMeds] = useState(null);
    const [review, setReview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [category, setCategory] = useState("Other");

    const first = name ? name.split(" ")[0] : "";
    const title = first ? `${first}'s Journey` : "Your Loved One's Journey";

    const loadDocs = () => api.get(`/care-records?session_id=${sid}`).then(r => setDocs(r.data.documents)).catch(() => setDocs([]));
    const loadLists = () => {
        api.get(`/care-records/timeline?session_id=${sid}`).then(r => setEvents(r.data.events)).catch(() => setEvents([]));
        api.get(`/care-team/providers/${sid}`).then(r => setProviders(r.data?.providers || r.data || [])).catch(() => setProviders([]));
        api.get(`/care-records/medications/list?session_id=${sid}`).then(r => setMeds(r.data.medications)).catch(() => setMeds([]));
    };
    useEffect(() => {
        api.get(`/profile/${sid}`).then(r => setName(r.data?.loved_one_name || "")).catch(() => {});
        api.get("/care-records/categories").then(r => setCats(r.data)).catch(() => {});
        loadDocs();
        loadLists();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("category", category);
            fd.append("session_id", sid);
            const r = await api.post("/care-records/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
            track(EVENTS.CARE_RECORD_UPLOADED, { category, analysis_status: r.data.analysis_status });
            setReview(r.data);
            loadDocs();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed — please try again.");
        } finally {
            setUploading(false);
        }
    };

    const legalDocs = useMemo(() => (docs || []).filter(d => LEGAL_CATS.includes(d.category)), [docs]);

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="loved-story-page" data-clarity-mask="true">
                <div className="mb-5"><JourneyViewToggle active="story" first={first} /></div>
                <p className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-dark">Everything that tells {first ? `${first}'s` : "their"} story is here</p>
                <h1 className="mt-1 ml-v2-serif text-4xl sm:text-5xl text-[#1F2624]" data-testid="loved-story-title">{title}</h1>
                <p className="mt-2 text-base text-[#4C5551]">How you got here — the Roadmap shows where you&apos;re going. One story. Every provider. Every transition.</p>

                <div className="mt-7 flex flex-wrap gap-2" data-testid="loved-story-tabs">
                    {TABS.map(({ key, label, Icon }) => (
                        <button key={key} onClick={() => setTab(key)} data-testid={`story-tab-${key}`}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-medium border transition-colors ${tab === key ? "bg-teal-dark text-white border-teal-dark" : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-light"}`}>
                            <Icon size={14} strokeWidth={1.8} /> {label}
                        </button>
                    ))}
                </div>

                {tab === "documents" && (
                    <div className="mt-6">
                        <div className="rounded-3xl border border-dashed border-teal/40 bg-teal-soft/20 p-6 flex flex-wrap items-center gap-4" data-testid="story-upload-zone">
                            <div className="flex-1 min-w-[220px]">
                                <p className="font-heading font-medium text-[#1F2624]">Add to {first ? `${first}'s` : "the"} story</p>
                                <p className="text-sm text-[#4C5551] mt-0.5">Visit notes, labs, imaging, discharge papers — as a PDF or a photo (JPG, PNG, HEIC). Lane reads it, you approve what changes. Originals are always kept.</p>
                            </div>
                            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="story-upload-category"
                                className="px-3 py-2 rounded-2xl bg-white border border-[#E2E8F0] text-sm">
                                {[...cats.categories, ...cats.custom].map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <label className={`inline-flex items-center gap-2 rounded-2xl bg-teal-dark text-white font-semibold px-6 py-3 text-sm cursor-pointer hover:bg-teal transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="story-upload-btn">
                                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploading ? "Lane is reading…" : "Upload a document"}
                                <input type="file" className="hidden" onChange={onUpload} accept=".pdf,.jpg,.jpeg,.png,.heic" data-testid="story-upload-input" />
                            </label>
                        </div>
                        <DocList docs={docs} onChanged={loadDocs} onReview={setReview} />
                    </div>
                )}

                {tab === "timeline" && <TimelineList events={events} first={first} />}
                {tab === "providers" && (
                    <div className="mt-6 space-y-2" data-testid="story-providers">
                        {(providers || []).length === 0 && <Empty text={`No providers yet. Upload a visit note and Lane will find them, or add them in Care Team.`} />}
                        {(providers || []).map(p => (
                            <div key={p.id} className="rounded-2xl border border-[#E2E8F0] bg-white px-4 py-3 flex items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-medium text-[#1F2624]">{p.name}</p>
                                    <p className="text-xs text-[#6E7A74]">{[p.role, p.organization, p.phone].filter(Boolean).join(" · ")}</p>
                                </div>
                                {p.source === "care_record" && <span className="text-[10px] uppercase tracking-wide font-semibold text-teal-dark bg-teal-soft rounded-2xl px-2 py-0.5">From a document</span>}
                            </div>
                        ))}
                        <Link to="/care-team" className="inline-flex items-center gap-1 text-sm font-medium text-teal-dark hover:underline mt-2">Open full Care Team <ChevronRight size={14} /></Link>
                    </div>
                )}
                {tab === "medications" && (
                    <div className="mt-6 space-y-2" data-testid="story-medications">
                        {(meds || []).length === 0 && <Empty text="No medications on record yet. Approve them from an uploaded visit note and they'll appear here, with every change tracked over time." />}
                        {(meds || []).map(m => (
                            <div key={m.id} className="rounded-2xl border border-[#E2E8F0] bg-white px-4 py-3 flex items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm text-[#1F2624]"><span className="font-medium">{m.name}</span> {m.dose} {m.frequency ? `· ${m.frequency}` : ""}</p>
                                    {m.effective_date && <p className="text-xs text-[#9CA6A1] mt-0.5">as of {m.effective_date}</p>}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {m.conflict && <span className="text-[10px] uppercase tracking-wide font-semibold rounded-2xl px-2 py-0.5 bg-[#FDF3D7] text-[#8A6D1A]" data-testid={`med-conflict-${m.name_key || m.id}`}>conflicting records</span>}
                                    <span className={`text-[10px] uppercase tracking-wide font-semibold rounded-2xl px-2 py-0.5 ${(m.status || m.change) === "discontinued" ? "bg-coral-light text-coral-dark" : "bg-teal-soft text-teal-dark"}`}>{m.status || m.change || "active"}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {tab === "legal" && <DocList docs={legalDocs} onChanged={loadDocs} onReview={setReview} emptyText="Nothing here yet. Upload POA, advance directives, or insurance papers and they'll live safely here." />}
                {tab === "summary" && (
                    <div className="mt-6 rounded-3xl border border-[#E2E8F0] bg-white p-6" data-testid="story-summary">
                        <p className="text-sm text-[#2A3330] leading-relaxed">Your Care Team Summary weaves everything here — approved documents, behavior notes, wellbeing, medications, timeline — into the one page you share before every appointment.</p>
                        <Link to="/care-summary" data-testid="story-open-summary" className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-teal-dark text-white font-semibold px-6 py-3 text-sm hover:bg-teal transition-colors">Open Care Summary <ChevronRight size={14} /></Link>
                    </div>
                )}

                <ReviewDialog review={review} first={first} onClose={() => { setReview(null); loadDocs(); loadLists(); }} sid={sid} />
            </div>
    );
}

function Empty({ text }) {
    return <p className="rounded-2xl border border-dashed border-[#E2E8F0] bg-cream-light/50 px-4 py-6 text-sm text-[#6E7A74] text-center">{text}</p>;
}

function TimelineList({ events, first }) {
    if (!events) return null;
    return (
        <div className="mt-6" data-testid="story-timeline">
            {events.length === 0 && <Empty text={`${first ? `${first}'s` : "The"} timeline builds itself as you approve events from uploaded documents.`} />}
            <ol className="relative border-l-2 border-teal/25 ml-2 space-y-5">
                {events.map(e => (
                    <li key={e.id} className="ml-5 relative">
                        <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-teal border-2 border-white" />
                        <p className="text-xs font-semibold uppercase tracking-wider text-teal-dark">{e.date}</p>
                        <p className="text-sm text-[#2A3330] mt-0.5">{e.label}</p>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function DocList({ docs, onChanged, onReview, emptyText }) {
    if (!docs) return <div className="mt-6 flex items-center gap-2 text-[#6E7A74] text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
    const byCat = docs.reduce((m, d) => { (m[d.category] = m[d.category] || []).push(d); return m; }, {});
    return (
        <div className="mt-5 space-y-5" data-testid="story-doc-list">
            {docs.length === 0 && <Empty text={emptyText || "No documents yet — the story starts with the first upload."} />}
            {Object.entries(byCat).map(([cat, list]) => (
                <div key={cat}>
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">{cat}</p>
                    <div className="mt-2 space-y-2">
                        {list.map(d => <DocRow key={d.id} d={d} onChanged={onChanged} onReview={onReview} />)}
                    </div>
                </div>
            ))}
        </div>
    );
}

function DocRow({ d, onChanged, onReview }) {
    const [busy, setBusy] = useState(false);
    const download = async () => {
        const r = await api.get(`/care-records/${d.id}/download`, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = Object.assign(document.createElement("a"), { href: url, download: d.filename });
        a.click(); URL.revokeObjectURL(url);
    };
    const cycleShare = async (opt) => {
        const cur = d.share_with || ["only_me"];
        const next = cur.includes(opt) ? cur.filter(x => x !== opt) : [...cur.filter(x => x !== "only_me"), opt];
        await api.patch(`/care-records/${d.id}`, { share_with: opt === "only_me" ? ["only_me"] : next });
        onChanged();
    };
    return (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white px-4 py-3" data-testid="story-doc-row">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1F2624] truncate">{d.filename}</p>
                    <p className="text-xs text-[#9CA6A1]">{new Date(d.uploaded_at).toLocaleDateString()} · {(d.size / 1024).toFixed(0)} KB{d.analysis_status !== "done" ? " · stored (no analysis)" : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                    {d.analysis_status === "done" && (
                        <button onClick={async () => { const r = await api.get(`/care-records/${d.id}`); onReview(r.data); }} data-testid="doc-review-btn"
                            className="text-xs font-semibold text-teal-dark bg-teal-soft rounded-2xl px-3 py-1.5 hover:bg-teal-soft/70">Lane's review</button>
                    )}
                    <button onClick={download} aria-label="Download original" data-testid="doc-download-btn" className="p-2 rounded-full hover:bg-cream-light text-[#4C5551]"><Download size={15} /></button>
                    <button disabled={busy} aria-label="Delete" data-testid="doc-delete-btn" className="p-2 rounded-full hover:bg-coral-light/40 text-[#9CA6A1] hover:text-coral-dark"
                        onClick={async () => { setBusy(true); await api.delete(`/care-records/${d.id}`); onChanged(); }}>
                        <Trash2 size={15} />
                    </button>
                </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(SHARE_LABELS).map(([opt, label]) => (
                    <button key={opt} onClick={() => cycleShare(opt)} data-testid={`doc-share-${opt}`}
                        className={`text-[11px] rounded-2xl px-2.5 py-1 border transition-colors ${(d.share_with || ["only_me"]).includes(opt) ? "bg-teal-dark text-white border-teal-dark" : "bg-white text-[#6E7A74] border-[#E2E8F0] hover:border-teal/40"}`}>
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );
}

const WRITES_TO = {
    stage: "Roadmap · Care Summary · Ask Lane",
    diagnosis: "Care Summary · Ask Lane",
    provider: "Care Team · Care Summary",
    medication: "Medication list · Care Summary · Ask Lane",
    timeline: "Journey timeline · Care Summary",
    recommendation: "Journey timeline · visit prep",
};

function ActionRow({ type, index, title, detail, evidence, verb, blocked, blockedLabel, done, out, onApprove, onDismiss, children }) {
    const grey = blocked || out;
    return (
        <div className={`rounded-2xl border border-[#E2E8F0] px-4 py-3 ${grey ? "bg-cream-light/60 opacity-75" : "bg-white"}`} data-testid={`review-action-${type}-${index}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${grey ? "text-[#6E7A74]" : "text-[#1F2624]"}`}>{title}</p>
                    {detail && <p className="text-xs text-[#6E7A74] mt-0.5">{detail}</p>}
                    {children}
                    {evidence && <p className="text-xs text-[#8A6D1A] mt-1 italic" data-testid={`review-evidence-${type}-${index}`}>&ldquo;{evidence}&rdquo;</p>}
                    {blocked
                        ? <p className="text-[11px] font-semibold text-[#8A6D1A] mt-1.5" data-testid={`review-blocked-${type}-${index}`}>{blockedLabel}</p>
                        : !out && !done && <p className="text-[10px] uppercase tracking-wide font-semibold text-teal-dark mt-1.5">Writes to: {WRITES_TO[type]}</p>}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1.5">
                    {done && <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-dark"><Check size={13} /> Done</span>}
                    {!done && out && <span className="text-xs font-semibold text-[#9CA6A1]">Dismissed</span>}
                    {!done && !out && !blocked && (
                        <>
                            <button onClick={onApprove} data-testid={`review-approve-${type}-${index}`}
                                className="text-xs font-semibold rounded-2xl bg-teal-dark text-white px-3.5 py-1.5 hover:bg-teal transition-colors">{verb}</button>
                            <button onClick={onDismiss} data-testid={`review-dismiss-${type}-${index}`}
                                className="text-[11px] text-[#6E7A74] hover:text-coral-dark underline">Not right</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function MedActionRow({ m, index, done, out, approve, dismiss }) {
    const [name, setName] = useState(m.name || "");
    const [dose, setDose] = useState(m.dose || "");
    const key = `medication-${index}`;
    const mentioned = m.status === "mentioned_not_prescribed";
    const unverified = m.verified === false;
    const statusLabel = m.status === "discontinued" ? "discontinued" : (mentioned ? "discussed only" : (m.change && m.change !== "unchanged" ? m.change : "active"));
    return (
        <ActionRow type="medication" index={index}
            title={`${m.name} ${m.dose || ""} — ${statusLabel}${m.clinical_date ? ` (as of ${m.clinical_date})` : ""}`}
            detail={m.frequency} evidence={m.evidence} verb="Update meds"
            blocked={mentioned || unverified}
            blockedLabel={mentioned ? "Discussed — not prescribed. Won't enter the medication list." : "Couldn't verify this in the document — check the original."}
            done={done} out={out}
            onApprove={() => approve("medication", index, key, { name: name.trim(), dose: dose.trim() })}
            onDismiss={() => dismiss("medication", index, key)}>
            {!mentioned && !unverified && !done && !out && (
                <div className="flex flex-wrap gap-2 mt-2">
                    <input value={name} onChange={(e) => setName(e.target.value)} data-testid={`review-med-name-${index}`}
                        aria-label="Medication name" className="px-2.5 py-1 rounded-lg border border-[#E2E8F0] text-xs w-40" />
                    <input value={dose} onChange={(e) => setDose(e.target.value)} data-testid={`review-med-dose-${index}`}
                        aria-label="Dose" placeholder="dose" className="px-2.5 py-1 rounded-lg border border-[#E2E8F0] text-xs w-24" />
                </div>
            )}
        </ActionRow>
    );
}

function ReviewDialog({ review, first, onClose, sid }) {
    const [applied, setApplied] = useState({});
    const [dismissed, setDismissed] = useState({});
    useEffect(() => {
        if (!review) return;
        const ap = {}; (review.applied_actions || []).forEach(x => { ap[`${x.type}-${x.index}`] = true; });
        const di = {}; (review.dismissed_actions || []).forEach(x => { di[`${x.type}-${x.index}`] = true; });
        setApplied(ap); setDismissed(di);
    }, [review]);
    const a = review?.analysis;
    if (!review) return null;

    const dxList = (a?.diagnosis || []).map(d => (typeof d === "string" ? { name: d } : d));

    const approve = async (type, index, key, overrides) => {
        try {
            const r = await api.post(`/care-records/${review.id}/apply`, { actions: [{ type, index, overrides }] });
            if ((r.data.applied || []).length === 0) { toast.error("This item can't be added — check the original document."); return; }
            track(EVENTS.CARE_RECORD_FACT_APPROVED, { fact_type: type });
            setApplied(s => ({ ...s, [key]: true }));
            toast.success("Approved — only this item was written.");
        } catch { toast.error("Could not apply — try again."); }
    };
    const dismiss = async (type, index, key) => {
        try {
            await api.post(`/care-records/${review.id}/dismiss`, { actions: [{ type, index }] });
            setDismissed(s => ({ ...s, [key]: true }));
        } catch { toast.error("Could not dismiss — try again."); }
    };
    const allActions = a ? [
        ...(a.stage?.key ? [{ type: "stage", index: 0 }] : []),
        ...dxList.map((_, i) => ({ type: "diagnosis", index: i })),
        ...(a.providers || []).map((_, i) => ({ type: "provider", index: i })),
        ...(a.medications || []).map((_, i) => ({ type: "medication", index: i })),
        ...(a.timeline_events || []).map((_, i) => ({ type: "timeline", index: i })),
        ...(a.recommendations || []).map((_, i) => ({ type: "recommendation", index: i })),
    ] : [];
    const rejectAll = async () => {
        const pending = allActions.filter(x => !applied[`${x.type}-${x.index}`] && !dismissed[`${x.type}-${x.index}`]);
        if (!pending.length) { onClose(); return; }
        try {
            await api.post(`/care-records/${review.id}/dismiss`, { actions: pending });
            setDismissed(s => { const n = { ...s }; pending.forEach(x => { n[`${x.type}-${x.index}`] = true; }); return n; });
            toast.success("Nothing was added. The original stays safely stored.");
        } catch { toast.error("Could not dismiss — try again."); }
    };
    const st = (t, i) => ({ done: !!applied[`${t}-${i}`], out: !!dismissed[`${t}-${i}`] });

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xl w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto rounded-3xl bg-cream p-6 md:p-7" aria-describedby={undefined} data-testid="review-dialog">
                <DialogTitle className="font-heading font-semibold text-xl text-[#1F2624]">
                    {a ? `I've learned something new about ${first || "your loved one"}.` : "Stored safely."}
                </DialogTitle>
                {!a && <p className="mt-2 text-sm text-[#4C5551]">The original is preserved and downloadable.{review.analysis_status === "no_text" ? " This PDF looks like a scan without readable text, so Lane couldn't pull details from it — a photo of the page often works better." : " Lane couldn't finish reading this one — you can delete it and try uploading again."}</p>}
                {a && (
                    <>
                        <p className="mt-1.5 text-sm text-[#4C5551]">{a.summary_line}</p>
                        <p className="mt-2 text-xs text-[#6E7A74]">Nothing below is saved anywhere until you approve it — each item commits on its own, and only where it says it will.</p>
                        <p className="mt-4 text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">Here&apos;s what I learned</p>
                        <ul className="mt-2 space-y-1">
                            {(a.findings || []).map(f => (
                                <li key={f} className="flex items-start gap-2 text-sm text-[#2A3330]"><Check size={14} className="text-teal-dark mt-0.5 shrink-0" /> {f}</li>
                            ))}
                        </ul>
                        <p className="mt-5 text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">Here&apos;s what this changes — approve each item on its own</p>
                        <div className="mt-2 space-y-2" data-testid="review-actions">
                            {a.stage?.key && <ActionRow type="stage" index={0} title={`Update ${first ? `${first}'s` : "the"} Roadmap → ${a.stage.label}`} evidence={a.stage.evidence} verb="Update roadmap" {...st("stage", 0)} onApprove={() => approve("stage", 0, "stage-0")} onDismiss={() => dismiss("stage", 0, "stage-0")} />}
                            {dxList.map((d, i) => <ActionRow key={`d${i}`} type="diagnosis" index={i} title={`Add diagnosis: ${d.name}`} evidence={d.evidence} verb="Add diagnosis" blocked={d.verified === false} blockedLabel="Couldn't verify this in the document — check the original." {...st("diagnosis", i)} onApprove={() => approve("diagnosis", i, `diagnosis-${i}`)} onDismiss={() => dismiss("diagnosis", i, `diagnosis-${i}`)} />)}
                            {(a.providers || []).map((p, i) => <ActionRow key={`p${i}`} type="provider" index={i} title={`Add ${p.name} to the Care Team`} detail={[p.specialty, p.clinic, p.phone].filter(Boolean).join(" · ")} evidence={p.evidence} verb="Add provider" blocked={p.verified === false} blockedLabel="Couldn't verify this in the document — check the original." {...st("provider", i)} onApprove={() => approve("provider", i, `provider-${i}`)} onDismiss={() => dismiss("provider", i, `provider-${i}`)} />)}
                            {(a.medications || []).map((m, i) => <MedActionRow key={`m${i}`} m={m} index={i} {...st("medication", i)} approve={approve} dismiss={dismiss} />)}
                            {(a.timeline_events || []).map((e, i) => <ActionRow key={`t${i}`} type="timeline" index={i} title={`Timeline: ${e.label}`} detail={e.date} verb="Add to timeline" {...st("timeline", i)} onApprove={() => approve("timeline", i, `timeline-${i}`)} onDismiss={() => dismiss("timeline", i, `timeline-${i}`)} />)}
                        </div>
                        {((a.recommendations || []).length > 0 || a.experience_note) && (
                            <>
                                <p className="mt-5 text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">Here&apos;s what I&apos;d recommend next</p>
                                <div className="mt-2 space-y-2" data-testid="review-recommendations">
                                    {(a.recommendations || []).map((r, i) => <ActionRow key={`r${i}`} type="recommendation" index={i} title={r} detail="Adds a discussion topic to the timeline & visit prep" verb="Add" {...st("recommendation", i)} onApprove={() => approve("recommendation", i, `recommendation-${i}`)} onDismiss={() => dismiss("recommendation", i, `recommendation-${i}`)} />)}
                                </div>
                                {a.experience_note && <p className="mt-3 text-sm text-teal-dark bg-teal-soft/40 rounded-2xl px-4 py-3 leading-relaxed">{a.experience_note}</p>}
                            </>
                        )}
                    </>
                )}
                <div className="mt-5 flex gap-2">
                    {a && <button onClick={rejectAll} data-testid="review-reject-all" className="flex-1 rounded-full border border-coral-light bg-white py-2.5 text-sm font-medium text-coral-dark hover:bg-coral-light/30">Reject all</button>}
                    <button onClick={onClose} data-testid="review-done" className="flex-1 rounded-full border border-[#E2E8F0] bg-white py-2.5 text-sm font-medium text-[#2A3330] hover:bg-cream-light">Done</button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
